// 2026-06-30: Phase 14 — Recipe Master (NADCAP Common Core item 2, second half)
// Defines qualified process recipes (parameter sets) per operation type.
// Recipes are bound per-operation at WO release (snapshot frozen for traceability).
// Data lives in customer SERVER_SCOPE attribute `recipe_master` (array).
import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { customerbasedshift, cleanCustomerId } from "../../Services/app/operatorservice";
import { shiftadd } from "../../Services/app/masterservice";
import Swal from 'sweetalert2';
import {
  Dialog, DialogTitle, DialogContent, DialogActions, Button,
  TextField, MenuItem, Tooltip, IconButton,
} from '@mui/material';
import EditIcon from '@mui/icons-material/Edit';
import DeleteIcon from '@mui/icons-material/Delete';
import AddIcon from '@mui/icons-material/Add';
import ContentCopyIcon from '@mui/icons-material/ContentCopy';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import EditNoteIcon from '@mui/icons-material/EditNote';
import BlockIcon from '@mui/icons-material/Block';

const BR = {
  orange: '#f97316', blue: '#2563eb', green: '#16a34a', amber: '#f59e0b', red: '#dc2626',
  dark: '#111827', muted: '#6b7280', border: '#e5e7eb', bg: '#f9fafb',
};

const STATUS_INFO = {
  ACTIVE:   { color: BR.green, bg: '#dcfce7', label: 'ACTIVE',   icon: <CheckCircleIcon fontSize="small" /> },
  DRAFT:    { color: BR.amber, bg: '#fef3c7', label: 'DRAFT',    icon: <EditNoteIcon fontSize="small" /> },
  OBSOLETE: { color: BR.muted, bg: '#f3f4f6', label: 'OBSOLETE', icon: <BlockIcon fontSize="small" /> },
};
const STATUSES = ['ACTIVE', 'DRAFT', 'OBSOLETE'];

function fmtDate(d) {
  if (!d) return '—';
  const dt = new Date(d);
  if (isNaN(dt.getTime())) return '—';
  return dt.toLocaleDateString('en-IN', { day: '2-digit', month: 'short', year: 'numeric' });
}

const emptyParam = { name: '', target: '', min: '', max: '', unit: '' };

const emptyRecipe = {
  id: '',
  recipe_code: '',
  recipe_name: '',
  op_type: '',
  revision: 'A',
  status: 'DRAFT',
  parameters: [{ ...emptyParam }],
  approved_by: '',
  approved_at: '',
  notes: '',
};

export default function RecipeMaster() {
  const customerId = cleanCustomerId(localStorage.getItem('CustomerID') || window._env_.CUSTOMER_ID);
  const [items, setItems] = useState([]);
  const [opTypes, setOpTypes] = useState([]);   // [{code, name}]
  const [loading, setLoading] = useState(true);
  const [filter, setFilter] = useState('ALL');
  const [search, setSearch] = useState('');
  const [editOpen, setEditOpen] = useState(false);
  const [draft, setDraft] = useState(emptyRecipe);
  const [saving, setSaving] = useState(false);

  const refresh = useCallback(async () => {
    if (!customerId) return;
    setLoading(true);
    try {
      const [rRec, rOps] = await Promise.all([
        customerbasedshift(customerId, 'recipe_master').catch(() => []),
        customerbasedshift(customerId, 'operation_master').catch(() => []),
      ]);
      const v = Array.isArray(rRec?.[0]?.value) ? rRec[0].value : [];
      setItems(v);
      const ops = Array.isArray(rOps?.[0]?.value) ? rOps[0].value : [];
      setOpTypes(ops
        .filter(o => o && (o.code || o.op_type))
        .map(o => ({ code: o.code || o.op_type, name: o.name || o.op_name || o.code || o.op_type }))
      );
    } catch (e) {
      console.warn('recipe master fetch failed:', e?.message);
      setItems([]);
    } finally {
      setLoading(false);
    }
  }, [customerId]);

  useEffect(() => { refresh(); }, [refresh]);

  const totals = useMemo(() => {
    const out = { ALL: items.length, ACTIVE: 0, DRAFT: 0, OBSOLETE: 0 };
    items.forEach(i => { out[i.status] = (out[i.status] || 0) + 1; });
    return out;
  }, [items]);

  const opName = useCallback((code) => {
    const m = opTypes.find(o => o.code === code);
    return m ? m.name : code;
  }, [opTypes]);

  const filtered = useMemo(() => {
    const q = search.trim().toLowerCase();
    return items.filter(i => {
      if (filter !== 'ALL' && i.status !== filter) return false;
      if (!q) return true;
      return (i.recipe_code || '').toLowerCase().includes(q)
          || (i.recipe_name || '').toLowerCase().includes(q)
          || (i.op_type || '').toLowerCase().includes(q)
          || opName(i.op_type).toLowerCase().includes(q);
    }).sort((a, b) => {
      const order = { ACTIVE: 0, DRAFT: 1, OBSOLETE: 2 };
      const so = (order[a.status] ?? 9) - (order[b.status] ?? 9);
      if (so) return so;
      return (a.recipe_code || '').localeCompare(b.recipe_code || '');
    });
  }, [items, filter, search, opName]);

  function openAdd() {
    setDraft({ ...emptyRecipe, id: `rcp-${Date.now()}`, parameters: [{ ...emptyParam }] });
    setEditOpen(true);
  }
  function openEdit(item) {
    setDraft({
      ...emptyRecipe, ...item,
      parameters: (item.parameters && item.parameters.length) ? item.parameters.map(p => ({ ...emptyParam, ...p })) : [{ ...emptyParam }],
    });
    setEditOpen(true);
  }
  function openClone(item) {
    setDraft({
      ...emptyRecipe, ...item,
      id: `rcp-${Date.now()}`,
      recipe_code: `${item.recipe_code}-COPY`,
      status: 'DRAFT',
      approved_by: '', approved_at: '',
      parameters: (item.parameters || [{ ...emptyParam }]).map(p => ({ ...emptyParam, ...p })),
    });
    setEditOpen(true);
  }

  function updateParam(idx, key, val) {
    setDraft(d => ({ ...d, parameters: d.parameters.map((p, i) => i === idx ? { ...p, [key]: val } : p) }));
  }
  function addParam() {
    setDraft(d => ({ ...d, parameters: [...d.parameters, { ...emptyParam }] }));
  }
  function removeParam(idx) {
    setDraft(d => ({ ...d, parameters: d.parameters.length > 1 ? d.parameters.filter((_, i) => i !== idx) : d.parameters }));
  }

  async function save() {
    if (!draft.recipe_code.trim()) {
      Swal.fire({ icon: 'warning', title: 'Recipe code required', confirmButtonColor: BR.orange });
      return;
    }
    if (!draft.op_type) {
      Swal.fire({ icon: 'warning', title: 'Operation type required',
                  text: 'Pick which operation this recipe applies to', confirmButtonColor: BR.orange });
      return;
    }
    const dupe = items.find(i => i.id !== draft.id
      && (i.recipe_code || '').trim().toLowerCase() === draft.recipe_code.trim().toLowerCase()
      && (i.revision || '') === (draft.revision || ''));
    if (dupe) {
      Swal.fire({ icon: 'warning', title: 'Duplicate recipe',
                  text: `${draft.recipe_code} rev ${draft.revision} already exists`, confirmButtonColor: BR.orange });
      return;
    }
    if (draft.status === 'ACTIVE' && !draft.parameters.some(p => p.name.trim())) {
      Swal.fire({ icon: 'warning', title: 'Parameters required to activate',
                  text: 'An ACTIVE recipe needs at least one named parameter', confirmButtonColor: BR.orange });
      return;
    }
    setSaving(true);
    try {
      const clean = {
        ...draft,
        recipe_code: draft.recipe_code.trim(),
        parameters: draft.parameters.filter(p => p.name.trim()),
        approved_at: draft.status === 'ACTIVE' && !draft.approved_at ? new Date().toISOString() : draft.approved_at,
      };
      const exists = items.findIndex(i => i.id === clean.id);
      const next = exists === -1 ? [...items, clean] : items.map((it, idx) => idx === exists ? clean : it);
      await shiftadd({ recipe_master: next }, customerId, 'SERVER_SCOPE');
      setItems(next);
      setEditOpen(false);
      Swal.fire({ toast: true, position: 'top-end', timer: 1800, showConfirmButton: false,
                  icon: 'success', title: `${clean.recipe_code} saved` });
    } catch (e) {
      Swal.fire({ icon: 'error', title: 'Save failed',
                  text: e?.response?.data?.message || e.message, confirmButtonColor: BR.orange });
    } finally {
      setSaving(false);
    }
  }

  async function deleteItem(item) {
    const res = await Swal.fire({
      title: 'Delete recipe?',
      html: `<strong>${item.recipe_code}</strong> rev ${item.revision || '-'} · ${opName(item.op_type)}<br/>Work orders already released keep their frozen snapshot.`,
      icon: 'warning', showCancelButton: true,
      confirmButtonText: 'Delete', confirmButtonColor: BR.red,
      cancelButtonText: 'Cancel', reverseButtons: true, focusCancel: true,
    });
    if (!res.isConfirmed) return;
    try {
      const next = items.filter(i => i.id !== item.id);
      await shiftadd({ recipe_master: next }, customerId, 'SERVER_SCOPE');
      setItems(next);
      Swal.fire({ toast: true, position: 'top-end', timer: 1500, showConfirmButton: false, icon: 'success', title: 'Removed' });
    } catch (e) {
      Swal.fire({ icon: 'error', title: 'Delete failed', text: e.message, confirmButtonColor: BR.orange });
    }
  }

  const isEdit = items.some(i => i.id === draft.id);

  return (
    <div style={{ padding: 16, background: BR.bg, minHeight: '100vh' }}>
      {/* Header */}
      <div style={{ display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between', marginBottom: 14 }}>
        <div>
          <h1 style={{ margin: 0, fontSize: 22, fontWeight: 900, color: BR.dark }}>🧪 Recipe Master</h1>
          <div style={{ fontSize: 12, color: BR.muted, marginTop: 4 }}>
            Qualified process parameter sets, bound per-operation at WO release · NADCAP Common Core capability #2
          </div>
        </div>
        <button onClick={openAdd}
                style={{ display: 'inline-flex', alignItems: 'center', gap: 6, padding: '9px 16px',
                          background: BR.orange, color: '#fff', border: 'none', borderRadius: 6,
                          fontWeight: 800, fontSize: 13, cursor: 'pointer' }}>
          <AddIcon fontSize="small" /> Add Recipe
        </button>
      </div>

      {/* Summary tiles */}
      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(4, 1fr)', gap: 10, marginBottom: 14 }}>
        {[
          { key: 'ALL',      label: 'All', icon: '📋', color: BR.dark },
          { key: 'ACTIVE',   label: 'Active', icon: '✓', color: BR.green },
          { key: 'DRAFT',    label: 'Draft', icon: '✎', color: BR.amber },
          { key: 'OBSOLETE', label: 'Obsolete', icon: '∅', color: BR.muted },
        ].map(t => (
          <div key={t.key} onClick={() => setFilter(t.key)}
               style={{ background: '#fff', border: `2px solid ${filter === t.key ? t.color : BR.border}`,
                        borderRadius: 8, padding: '12px 14px', cursor: 'pointer',
                        boxShadow: filter === t.key ? `0 2px 8px ${t.color}33` : '0 1px 2px rgba(0,0,0,0.04)' }}>
            <div style={{ fontSize: 10, fontWeight: 800, color: BR.muted, textTransform: 'uppercase', letterSpacing: 0.5 }}>
              {t.icon} {t.label}
            </div>
            <div style={{ fontSize: 26, fontWeight: 900, color: t.color, lineHeight: 1.1, marginTop: 4 }}>
              {totals[t.key] ?? 0}
            </div>
          </div>
        ))}
      </div>

      {/* Search */}
      <div style={{ display: 'flex', gap: 10, marginBottom: 10 }}>
        <input value={search} onChange={e => setSearch(e.target.value)}
               placeholder="Search code, name, operation…"
               style={{ flex: 1, padding: '8px 12px', border: `1px solid ${BR.border}`, borderRadius: 6, fontSize: 13 }} />
      </div>

      {/* Table */}
      <div style={{ background: '#fff', border: `1px solid ${BR.border}`, borderRadius: 8, overflow: 'hidden' }}>
        {loading ? (
          <div style={{ padding: 40, textAlign: 'center', color: BR.muted }}>Loading…</div>
        ) : filtered.length === 0 ? (
          <div style={{ padding: 40, textAlign: 'center', color: BR.muted, fontSize: 13 }}>
            No recipes {filter !== 'ALL' ? `in ${filter} state` : 'yet'}. Click "Add Recipe" to define one.
          </div>
        ) : (
          <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 12 }}>
            <thead>
              <tr style={{ background: BR.bg, borderBottom: `1px solid ${BR.border}` }}>
                {['Status', 'Recipe Code', 'Name', 'Operation', 'Rev', 'Params', 'Approved By', 'Approved', ''].map(h => (
                  <th key={h} style={{ padding: '9px 10px', textAlign: 'left', fontSize: 10, fontWeight: 800,
                                        color: BR.muted, textTransform: 'uppercase', letterSpacing: 0.4 }}>{h}</th>
                ))}
              </tr>
            </thead>
            <tbody>
              {filtered.map(i => {
                const info = STATUS_INFO[i.status] || STATUS_INFO.DRAFT;
                return (
                  <tr key={i.id} style={{ borderBottom: `1px solid ${BR.border}` }}>
                    <td style={{ padding: '9px 10px' }}>
                      <span style={{ display: 'inline-flex', alignItems: 'center', gap: 4, padding: '2px 8px',
                                      borderRadius: 4, fontWeight: 800, fontSize: 10,
                                      background: info.bg, color: info.color, border: `1px solid ${info.color}` }}>
                        {info.icon} {info.label}
                      </span>
                    </td>
                    <td style={{ padding: '9px 10px', fontFamily: 'monospace', fontWeight: 800 }}>{i.recipe_code}</td>
                    <td style={{ padding: '9px 10px' }}>{i.recipe_name || '—'}</td>
                    <td style={{ padding: '9px 10px' }}>
                      <span style={{ fontFamily: 'monospace', color: BR.blue, fontWeight: 700 }}>{i.op_type}</span>
                      <span style={{ color: BR.muted }}> · {opName(i.op_type)}</span>
                    </td>
                    <td style={{ padding: '9px 10px', fontWeight: 700 }}>{i.revision || '—'}</td>
                    <td style={{ padding: '9px 10px', textAlign: 'center' }}>
                      <span style={{ background: '#eef2ff', color: BR.blue, borderRadius: 10, padding: '1px 8px', fontWeight: 800 }}>
                        {(i.parameters || []).filter(p => p.name).length}
                      </span>
                    </td>
                    <td style={{ padding: '9px 10px', color: BR.muted }}>{i.approved_by || '—'}</td>
                    <td style={{ padding: '9px 10px', color: BR.muted }}>{fmtDate(i.approved_at)}</td>
                    <td style={{ padding: '9px 10px', textAlign: 'right', whiteSpace: 'nowrap' }}>
                      <Tooltip title="Clone"><IconButton size="small" onClick={() => openClone(i)}>
                        <ContentCopyIcon fontSize="small" sx={{ color: BR.blue }} />
                      </IconButton></Tooltip>
                      <Tooltip title="Edit"><IconButton size="small" onClick={() => openEdit(i)}>
                        <EditIcon fontSize="small" sx={{ color: '#111' }} />
                      </IconButton></Tooltip>
                      <Tooltip title="Delete"><IconButton size="small" onClick={() => deleteItem(i)}>
                        <DeleteIcon fontSize="small" sx={{ color: BR.red }} />
                      </IconButton></Tooltip>
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        )}
      </div>

      <div style={{ marginTop: 12, fontSize: 11, color: BR.muted, padding: '8px 12px',
                     background: '#fff7ed', border: `1px solid #fed7aa`, borderRadius: 6 }}>
        <strong style={{ color: '#9a3412' }}>NADCAP note:</strong> Only <strong>ACTIVE</strong> recipes can be bound to an operation.
        At work-order release the recipe's parameters are frozen onto the operation as an immutable snapshot, so later edits or obsoletion never alter released travelers.
      </div>

      {/* Add / Edit dialog */}
      <Dialog open={editOpen} onClose={() => setEditOpen(false)} maxWidth="md" fullWidth>
        <DialogTitle sx={{ fontWeight: 800 }}>{isEdit ? `Edit ${draft.recipe_code || 'Recipe'}` : 'Add Recipe'}</DialogTitle>
        <DialogContent dividers>
          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(2, 1fr)', gap: 12 }}>
            <TextField label="Recipe Code *" size="small" fullWidth value={draft.recipe_code}
                       onChange={e => setDraft({ ...draft, recipe_code: e.target.value })} placeholder="e.g. HT-CARBURIZE-01" />
            <TextField label="Recipe Name" size="small" fullWidth value={draft.recipe_name}
                       onChange={e => setDraft({ ...draft, recipe_name: e.target.value })} placeholder="e.g. Carburize 1020°C / 6h" />
            <TextField label="Operation Type *" size="small" fullWidth select value={draft.op_type}
                       onChange={e => setDraft({ ...draft, op_type: e.target.value })}
                       helperText={opTypes.length ? '' : 'No operation_master found — type a code'}>
              <MenuItem value="">— Select —</MenuItem>
              {opTypes.map(o => <MenuItem key={o.code} value={o.code}>{o.code} · {o.name}</MenuItem>)}
            </TextField>
            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 12 }}>
              <TextField label="Revision" size="small" fullWidth value={draft.revision}
                         onChange={e => setDraft({ ...draft, revision: e.target.value })} placeholder="A" />
              <TextField label="Status" size="small" fullWidth select value={draft.status}
                         onChange={e => setDraft({ ...draft, status: e.target.value })}>
                {STATUSES.map(s => <MenuItem key={s} value={s}>{s}</MenuItem>)}
              </TextField>
            </div>
            <TextField label="Approved By" size="small" fullWidth value={draft.approved_by}
                       onChange={e => setDraft({ ...draft, approved_by: e.target.value })} placeholder="Quality engineer" />
            <TextField label="Notes" size="small" fullWidth multiline minRows={1} value={draft.notes}
                       onChange={e => setDraft({ ...draft, notes: e.target.value })} sx={{ gridColumn: '1 / span 2' }} />
          </div>

          {/* Parameters editor */}
          <div style={{ marginTop: 18 }}>
            <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 8 }}>
              <div style={{ fontSize: 13, fontWeight: 800, color: BR.dark }}>Process Parameters</div>
              <Button onClick={addParam} size="small" startIcon={<AddIcon />}
                      sx={{ color: BR.blue, textTransform: 'none', fontWeight: 700 }}>Add parameter</Button>
            </div>
            <div style={{ border: `1px solid ${BR.border}`, borderRadius: 6, overflow: 'hidden' }}>
              <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 12 }}>
                <thead>
                  <tr style={{ background: BR.bg }}>
                    {['Parameter', 'Target', 'Min', 'Max', 'Unit', ''].map(h => (
                      <th key={h} style={{ padding: '6px 8px', textAlign: 'left', fontSize: 10, fontWeight: 800,
                                            color: BR.muted, textTransform: 'uppercase' }}>{h}</th>
                    ))}
                  </tr>
                </thead>
                <tbody>
                  {draft.parameters.map((p, idx) => (
                    <tr key={idx} style={{ borderTop: `1px solid ${BR.border}` }}>
                      <td style={{ padding: '4px 6px' }}>
                        <input value={p.name} onChange={e => updateParam(idx, 'name', e.target.value)}
                               placeholder="e.g. Temperature"
                               style={{ width: '100%', border: 'none', padding: 6, fontSize: 12, outline: 'none' }} />
                      </td>
                      {['target', 'min', 'max'].map(k => (
                        <td key={k} style={{ padding: '4px 6px', width: 90 }}>
                          <input value={p[k]} onChange={e => updateParam(idx, k, e.target.value)}
                                 style={{ width: '100%', border: 'none', padding: 6, fontSize: 12, outline: 'none', textAlign: 'right' }} />
                        </td>
                      ))}
                      <td style={{ padding: '4px 6px', width: 80 }}>
                        <input value={p.unit} onChange={e => updateParam(idx, 'unit', e.target.value)}
                               placeholder="°C"
                               style={{ width: '100%', border: 'none', padding: 6, fontSize: 12, outline: 'none' }} />
                      </td>
                      <td style={{ padding: '4px 6px', width: 36, textAlign: 'center' }}>
                        <IconButton size="small" onClick={() => removeParam(idx)} disabled={draft.parameters.length <= 1}>
                          <DeleteIcon fontSize="small" sx={{ color: draft.parameters.length <= 1 ? BR.border : BR.red }} />
                        </IconButton>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
            <div style={{ fontSize: 11, color: BR.muted, marginTop: 6 }}>
              Min/Max define the acceptance window the operator must stay within. Leave blank if not applicable.
            </div>
          </div>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setEditOpen(false)}>Cancel</Button>
          <Button onClick={save} disabled={saving} variant="contained"
                  sx={{ background: BR.orange, '&:hover': { background: '#ea580c' } }}>
            {saving ? 'Saving…' : 'Save'}
          </Button>
        </DialogActions>
      </Dialog>
    </div>
  );
}
